import React, { useEffect, useState } from 'react'
import * as PhosphorIcons from '@phosphor-icons/react'
import consumer from '../channels/consumer'
import { toast } from '../utils'
const NewProjectSetup = ({
  agents,
  suggest_agents_projects_path,
  create_project_path,
  project_path,
}) => {
  const [action, setAction] = useState('suggestions')
  const [suggestions, setSuggestions] = useState([])
  const [reason, setReason] = useState('')
  const [userMessage, setUserMessage] = useState('')
  const [selectedAgents, setSelectedAgents] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [filteredAgents, setFilteredAgents] = useState(agents)
  const [isClearingState, setIsClearingState] = useState(false)

  useEffect(() => {
    const subscription = consumer.subscriptions.create('RagResponseChannel', {
      received (search_result) {
        if (search_result.type === 'suggest_agents_project_rag_chat_results') {
          const newAgents = search_result.agents || []
          const newReason = search_result.reason || ''
          setSuggestions(newAgents)
          setReason(newReason)
          setIsLoading(false)
        }
      },
    })

    return () => {
      subscription.unsubscribe()
      setIsLoading(false)
    }
  }, [])

  const toggleAgent = (agentId) => {
    setSelectedAgents((prevSelectedAgents) =>
      prevSelectedAgents.includes(agentId)
        ? prevSelectedAgents.filter((id) => id !== agentId)
        : [...prevSelectedAgents, agentId],
    )
  }


  const handleSuggestAgents = () => {
    if (isClearingState) {
      setUserMessage('')
      setSuggestions([])
      setReason('')
      setIsClearingState(false)
      return
    }

    setIsLoading(true)
    setIsClearingState(true)
    fetch(suggest_agents_projects_path, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
      },
      body: JSON.stringify({
        query: userMessage,
      }),
    })
  }

  const handleCreateProject = async () => {
    try {
      const response = await fetch(create_project_path, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').
            getAttribute('content'),
        },
        body: JSON.stringify({
          project: {
            agent_ids: selectedAgents,
            suggestions: suggestions,
          },
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        console.error('Failed to create project:',
          errorData.message || 'Unknown error')
        return
      }

      const data = await response.json()
      if (data.project?.id) {
        toast({message: 'Project created successfully', type: 'success', duration: 3000, gravity: 'top', position: 'right', close: true})
        Turbo.visit(project_path?.replace(':id', data.project.id))
      }
    } catch (error) {
      console.error('Error creating project:', error)
    }
  }

  const handleSearch = (query) => {
    
    setSearchQuery(query)
    const lowercaseQuery = query.toLowerCase()
    setFilteredAgents(agents.filter(agent => {
      return (
        agent.label?.toLowerCase().includes(lowercaseQuery) ||
        agent.short_label?.toLowerCase().includes(lowercaseQuery) ||
        agent.description?.toLowerCase().includes(lowercaseQuery) ||
        agent.agent_type?.toLowerCase().includes(lowercaseQuery) ||
        agent.industry?.some(ind => ind.toLowerCase().includes(lowercaseQuery))
      )
    }))
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && userMessage.length > 10 && !isLoading) {
      handleSuggestAgents()
    }
  }

  return (
    <div>
      <div className="flex gap-2 justify-center items-center p-2">
        <button onClick={() => setAction('suggestions')}
                className={`btn btn-outline ${action === 'suggestions'
                  ? 'btn-ghost btn-primary'
                  : ''}`}>
          <div className="flex items-center gap-2">
            <PhosphorIcons.Lightning size={32} color="teal"/>
            <span>Suggestions</span>
          </div>
        </button>
        <button onClick={() => setAction('agents')}
                className={`btn btn-outline ${action === 'agents'
                  ? 'btn-ghost btn-primary'
                  : ''}`}>
          <div className="flex items-center gap-2">
            <PhosphorIcons.Users size={32} color="teal"/>
            <span>Agents</span>
          </div>
        </button>
      </div>
      {action === 'suggestions' && (
        <div
          className="flex flex-col gap-4 items-center p-2 rounded-lg shadow-md bg-base-100">
          <input type="text"
                 value={userMessage}
                 onChange={(e) => setUserMessage(e.target.value)}
                 onKeyDown={handleKeyPress}
                 placeholder="Write here what is your problem and we will suggest agents to solve it"
                 className={`input input-bordered w-full max-w-2xl ${userMessage.length > 0 && userMessage.length <= 10 ? 'input-warning' : ''}`} />
          <button
            onClick={handleSuggestAgents}
            disabled={userMessage.length <= 10 || isLoading}
            className="btn btn-outline gap-2 items-center flex justify-center w-full max-w-2xl">
            {suggestions.length > 0 ? (
              <>
                <PhosphorIcons.X size={24} color="white"/>
                <span>Ask for a new suggestion</span>
              </>
            ) : (
              <>
                <span>Suggest Agents</span>
                <PhosphorIcons.ArrowRight size={24} color="white"/>
              </>
            )}
          </button>

          {isLoading ? (
            <div className="flex flex-col items-center gap-4 w-full max-w-2xl p-8">
              <PhosphorIcons.SpinnerGap size={48} className="animate-spin text-primary"/>
              <span className="text-lg font-medium">Suggesting agents...</span>
            </div>
          ) : reason && (
            <div className="flex flex-col gap-4 w-full max-w-2xl">
              {suggestions.length > 0 ? (
                <>
                  <h3 className="text-lg font-semibold">Suggested Agents:</h3>
                  {suggestions.map((suggestion) => (
                    <div key={suggestion}
                         className="flex items-center gap-2 p-3 rounded-lg border-2 border-base-300">
                      <PhosphorIcons.Lightning size={24} color="teal"/>
                      <span className="font-medium">{suggestion.replace(/_/g,
                        ' ')}</span>
                    </div>
                  ))}
                  <button
                    onClick={handleCreateProject}
                    disabled={suggestions.length === 0}
                    className="btn btn-primary w-full mt-2 btn-outline btn-ghost">
                    <PhosphorIcons.ArrowRight size={24} color="teal"/>
                    Create Project with Selected Agents
                  </button>
                </>
              ) : null}

              <div
                className="flex flex-col gap-2 w-full max-w-2xl p-4 rounded-lg border-2 border-base-300">
                <h3 className="text-lg font-semibold">Reason:</h3>
                <p className="text-base-content/80">{reason}</p>
              </div>
            </div>
          )}
        </div>
      )}
      {action === 'agents' && (
        <>
          <div
            className="flex flex-col gap-4 p-2 rounded-lg shadow-md bg-base-100">
              <div className="flex items-center gap-2">
              <input type="text"
                     value={searchQuery}
                     placeholder="Search agents description"
                     className={`input input-bordered w-full ${searchQuery.length <= 1 ? 'input-error' : ''}`}
                     onChange={(e) => handleSearch(e.target.value)}/>
                     {searchQuery.length > 0 && (
                      <button
                        onClick={() => {
                          setSearchQuery('')
                          handleSearch('')
                        }}
                        className="btn btn-outline btn-ghost">
                        <PhosphorIcons.X size={24} color="red"/>
                      </button>
                     )}
              </div>
            {filteredAgents.map((agent) => (
              <div
                className="flex flex-col p-4 rounded-lg border-2 border-base-300 hover:bg-base-200 transition-colors shadow-sm"
                key={agent.id}>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-4 flex-1">
                    <div className={`p-3 rounded-lg ${agent.bg_class}`}>
                      {PhosphorIcons[agent.icon] ?
                        React.createElement(PhosphorIcons[agent.icon], {
                          size: 24,
                          className: agent.color_class,
                        })
                        :
                        <PhosphorIcons.Tag size={24}
                                           className={agent.color_class}/>
                      }
                    </div>

                    <div className="flex flex-col gap-0.5">
                      <div className="flex items-center gap-2">
                      <span
                        className="text-lg font-semibold">{agent.label}</span>
                        <span
                          className="text-sm font-medium text-base-content/70">{agent.short_label}</span>
                      </div>
                      <span
                        className="text-sm font-medium text-base-content/70">{agent.agent_type}</span>
                      <span
                        className="text-sm text-base-content/60">{agent.description}</span>
                      <div className="flex gap-2 mt-1">
                        {agent.industry.map((ind) => (
                          <span key={ind}
                                className="shadow-md badge badge-lg p-3">{ind}</span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <label title={agent.tooltip}>
                    <input type="checkbox"
                           name="agents[]"
                           value={agent.id}
                           checked={selectedAgents.includes(agent.id)}
                           onChange={() => toggleAgent(agent.id)}
                           className="checkbox checkbox-lg border-2 checkbox-primary"
                    />
                  </label>
                </div>
              </div>

            ))}
          </div>
          <button
            onClick={handleCreateProject}
            disabled={selectedAgents.length === 0}
            className="btn btn-outline btn-ghost w-full mt-2 btn-primary">
            <PhosphorIcons.ArrowRight size={24} color="white"/>
            Create Project with Selected Agents
          </button>
        </>
      )}
    </div>
  )
}

export default NewProjectSetup